import * as React from "react";
import { Theme, createStyles, withStyles, WithStyles } from "@material-ui/core";
import { MouseEventCallback, GRIDH, GRIDV } from "./composer";

interface State {
	isDragging: boolean;
	isResizing: boolean;
}

export interface ComposerTableProps {
	id: string;
	maxX: number;
	maxY: number;
	initialX: number;
	initialY: number;
	gridSize: number;
	displayName: string;
	initialWidth: number;
	initialHeight: number;
	registerRef: (instance: ComposerTable) => void;
	registerMouseUpCallback: (cb: MouseEventCallback) => void;
	registerMouseMoveCallback: (cb: MouseEventCallback) => void;
}

interface ConnectedComposerTableProps extends WithStyles<typeof styles> {}

type Props = ComposerTableProps & ConnectedComposerTableProps;

export class ComposerTable extends React.Component<Props, State> {
	public state: State = {
		isDragging: false,
		isResizing: false
	};

	private curX: number = this.props.initialX;
	private curY: number = this.props.initialY;
	private lastX: number = this.props.initialX;
	private lastY: number = this.props.initialY;
	private startX: number = 0;
	private startY: number = 0;
	private curWidth: number = this.props.initialWidth;
	private curHeight: number = this.props.initialHeight;
	private lastWidth: number = this.props.initialWidth;
	private lastHeight: number = this.props.initialHeight;
	private ref: HTMLDivElement;

	public componentDidMount() {
		this.props.registerRef(this);

		this.props.registerMouseMoveCallback((mx, my) => {
			const { gridSize } = this.props;
			const { isDragging, isResizing } = this.state;

			if (isDragging) {
				const { x, y } = this.getOffset(mx, my);

				const addX =
					Math.floor(x / gridSize) +
					(x % gridSize < gridSize / 2 ? 0 : 1);
				const addY =
					Math.floor(y / gridSize) +
					(y % gridSize < gridSize / 2 ? 0 : 1);

				let newX = this.lastX + addX;
				let newY = this.lastY + addY;

				if (newX < 0) {
					newX = 0;
				} else if (newX > GRIDH - this.curWidth) {
					newX = GRIDH - this.curWidth;
				}

				if (newY < 0) {
					newY = 0;
				} else if (newY > GRIDV - this.curHeight) {
					newY = GRIDV - this.curHeight;
				}

				this.curX = newX;
				this.curY = newY;

				this.updatePosition();
			} else if (isResizing) {
				const { x, y } = this.getOffset(mx, my);

				const addWidth =
					Math.floor(x / gridSize) +
					(x % gridSize < gridSize / 2 ? 0 : 1);
				const addHeight =
					Math.floor(y / gridSize) +
					(y % gridSize < gridSize / 2 ? 0 : 1);

				let newWidth = this.lastWidth + addWidth;
				let newHeight = this.lastHeight + addHeight;

				newWidth = newWidth < 10 ? 10 : newWidth;
				newHeight = newHeight < 10 ? 10 : newHeight;

				this.curWidth = newWidth;
				this.curHeight = newHeight;

				this.updateSize();
			}
		});

		this.props.registerMouseUpCallback((mx, my) => {
			if (this.state.isDragging) {
				this.lastX = this.curX;
				this.lastY = this.curY;

				this.setState({ isDragging: false });
			} else if (this.state.isResizing) {
				this.lastWidth = this.curWidth;
				this.lastHeight = this.curHeight;

				this.setState({ isResizing: false });
			}
		});
	}

	private handleMouseDown = (event: React.MouseEvent) => {
		this.startX = event.clientX;
		this.startY = event.clientY;

		this.setState({ isDragging: true });
	};

	private handleResizeHandleMouseDown = (event: React.MouseEvent) => {
		event.stopPropagation();

		this.startX = event.clientX;
		this.startY = event.clientY;

		this.setState({ isResizing: true });
	};

	private getOffset = (x: number, y: number) => ({
		x: x - this.startX,
		y: y - this.startY
	});

	private updatePosition = () => {
		this.ref.style.top = `${this.curY * this.props.gridSize}px`;
		this.ref.style.left = `${this.curX * this.props.gridSize}px`;
	};

	private updateSize = () => {
		this.ref.style.width = `${this.curWidth * this.props.gridSize}px`;
		this.ref.style.height = `${this.curHeight * this.props.gridSize}px`;
	};

	public getInfo = () => ({
		id: this.props.id,
		x: this.curX,
		y: this.curY,
		width: this.curWidth,
		height: this.curHeight
	});

	public render() {
		const { classes, displayName, gridSize } = this.props;

		return (
			<div
				ref={i => (this.ref = i)}
				className={classes.root}
				onMouseDown={this.handleMouseDown}
				style={{
					top: this.curY * gridSize,
					left: this.curX * gridSize,
					width: this.curWidth * gridSize,
					height: this.curHeight * gridSize
				}}
			>
				<span>{displayName}</span>
				<br />
				<span>
					{this.curX} / {this.curY}
				</span>
				<br />
				<span>
					{this.curWidth} / {this.curHeight}
				</span>
				<div
					className={classes.resizeHandle}
					onMouseDown={this.handleResizeHandleMouseDown}
				/>
			</div>
		);
	}
}

const styles = (theme: Theme) =>
	createStyles({
		root: {
			position: "absolute",
			border: "1px solid black",
			userSelect: "none",
			cursor: "move"
		},
		resizeHandle: {
			position: "absolute",
			bottom: 0,
			right: 0,
			background: "black",
			width: 10,
			height: 10
		}
	});

export const ConnectedComposerTable = withStyles(styles)(ComposerTable);
